import { type VoidFunctionComponent } from 'react';

import { Checkbox } from '../../generic';
import { TooltipButton } from '../../generic/Button';
import Flex from '../../generic/Flex';
import Icon, { icons } from '../../generic/Icon';
import List from '../../generic/List';
import Tooltip from '../../generic/Tooltip';
import TooltipIcon from '../../generic/TooltipIcon';
import type { LicensingRuleType } from '../../../../shared/model-typings';
import type { LicensingRule } from '../../../types';

import styles from './Rules.module.less';

type RuleItemProps = {
  rule: LicensingRule;
  isChecked: boolean;
  ruleType?: LicensingRuleType;
  onCheck: (isChecked: boolean, licenseId: string, ruleType?: LicensingRuleType) => void;
  onLicenseClick: (licenseId: string | null, ruleId: number | null) => void;
  licenseName?: string;
};

//  Returns the text for the tooltip based on the specified depth condition.
function getDepthTooltip(depthCondition?: 'DIRECT' | 'DEEP'): string {
  if (depthCondition === 'DIRECT') {
    return 'found in your code';
  }
  if (depthCondition === 'DEEP') {
    return 'found in dependency';
  }
  return '';
}

function getNameConditionTooltip(nameCondition?: string): string {
  return nameCondition
    ? `name contains ${nameCondition}`
    : '';
}

function getLinkingTooltip(linkingCondition?: 'STATIC' | 'DYNAMIC'): string {
  if (linkingCondition === 'STATIC') {
    return 'statically linked';
  }
  if (linkingCondition === 'DYNAMIC') {
    return 'dynamically linked';
  }
  return '';
}

//  Build the condition tooltip.
function getConditionalTooltip(
  depthCondition?: 'DIRECT' | 'DEEP',
  nameCondition?: string,
  linkingCondition?: 'STATIC' | 'DYNAMIC',
): string {
  return [
    getDepthTooltip(depthCondition),
    getNameConditionTooltip(nameCondition),
    getLinkingTooltip(linkingCondition),
  ]
    .filter(value => !!value)
    .join(' & ');
}

export const RuleItem: VoidFunctionComponent<RuleItemProps> = (props) => {
  const { depthCondition, nameCondition, linkingCondition } = props.rule;

  const hasCondition = !!(depthCondition || nameCondition || linkingCondition);

  const conditionalTooltip = hasCondition
    ? `If ${getConditionalTooltip(depthCondition ?? undefined, nameCondition ?? undefined, linkingCondition ?? undefined)}.`
    : '';

  const checkbox = (
    <Checkbox
      isChecked={props.isChecked}
      onChange={(_, isChecked) => props.onCheck(isChecked, props.rule.licenseId, props.ruleType)}
      isRippleDisabled
      shouldStopPropagation
    />
  );

  const viewLicenseButton = (
    <div
      role="presentation"
      className={styles.viewLicenseButton}
      // Prevent the view license button click from toggling the checkbox.
      onClick={e => e.stopPropagation()}
    >
      <TooltipButton
        id={`rule-item-view-license-${props.rule.licenseId.toLowerCase()}`}
        tooltip="View License"
        size="micro"
        type="outline"
        icon={icons.eye}
        onClick={() => props.onLicenseClick(props.rule.licenseId ?? null, props.rule.ruleId ?? null)}
      />
    </div>
  );

  const content = (
    <Flex gap={8}>
      <div className={styles.ruleItemTitle}>
        {/* We should always be able to show the license name, but just in case we'll fallback to the license ID */}
        {props.licenseName ?? props.rule.licenseId}
      </div>
      {hasCondition && (
        <Tooltip
          id={`rule-item-tooltip-${props.rule.licenseId.toLowerCase()}`}
          tooltip={conditionalTooltip}
          showTooltip={hasCondition}
        >
          <span className="policy-rule-condition margin-left-xs">
            <Icon icon={icons.questionCircle} />
          </span>
        </Tooltip>
      )}
      {props.rule.notes && (
        <TooltipIcon
          id={`rule-item-notes-${props.rule.licenseId.toLowerCase()}`}
          icon={icons.noteRegular}
          tooltip={props.rule.notes}
        />
      )}
    </Flex>
  );

  return (
    <List.RowContainer>
      <List.Row
        left={checkbox}
        right={viewLicenseButton}
        // Allow clicking the row to toggle the checkbox in that row.
        onClick={() => props.onCheck(!props.isChecked, props.rule.licenseId, props.ruleType)}
      >
        {content}
      </List.Row>
    </List.RowContainer>
  );
};
